import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { authAPI } from '../services/api';

const schema = z.object({
  email: z.string().email('Please enter a valid email'),
});

export default function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data) => {
    try {
      await authAPI.forgotPassword(data.email);
      setSubmitted(true);
    } catch {
      setSubmitted(true); // Show success anyway to prevent email enumeration
    }
  };

  if (submitted) {
    return (
      <div className="animate-fade-in text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">Check your email</h2>
        <p className="text-neutral-600 mb-6">
          If an account exists with that email, we've sent password reset instructions.
        </p>
        <Link to="/login" className="btn-primary">Back to sign in</Link>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <Link to="/login" className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to sign in
      </Link>
      <h2 className="text-2xl font-bold text-neutral-900 mb-2">Forgot password?</h2>
      <p className="text-neutral-600 mb-6">Enter your email and we'll send you reset instructions.</p>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label htmlFor="email" className="label">Email address</label>
          <input {...register('email')} type="email" id="email" className={`input ${errors.email ? 'input-error' : ''}`} placeholder="you@company.com" />
          {errors.email && <p className="error-text">{errors.email.message}</p>}
        </div>
        <button type="submit" disabled={isSubmitting} className="w-full btn-primary py-3">
          {isSubmitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending...</> : 'Send reset link'}
        </button>
      </form>
    </div>
  );
}
